"use client";

import { useState } from "react";
import { Service } from "@/services/serviceService";
import styles from "./PaymentModal.module.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import crmApiService from "@/services/crmApiService";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: Service | null;
}

interface PaymentInfo {
  transaction: {
    transactionCode: string;
    amount: number;
    packageType: string;
    status: string;
  };
  bank: {
    bin: string;
    bankName: string;
    accountName: string;
    accountNumber: string;
    content: string;
    amount: number;
    vietQrUrl: string;
  };
}

export default function PaymentModal({
  isOpen,
  onClose,
  service,
}: PaymentModalProps) {
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({ fullName: "", phone: "", email: "" });

  const createPayment = async () => {
    if (!service) return;

    // Validation nhanh + toast lỗi
    if (!form.fullName.trim()) {
      toast.error("Vui lòng nhập họ tên");
      return;
    }
    if (!/^\d{9,11}$/.test(form.phone.trim())) {
      toast.error("Số điện thoại không hợp lệ (9-11 số)");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      toast.error("Email không hợp lệ");
      return;
    }

    const toastId = toast.loading("Đang tạo mã QR thanh toán...", {
      position: "top-center",
    });

    setIsLoading(true);

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:7001/api';
      const response = await fetch(
        `${apiUrl}/payments/bank/create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            packageType: service.type,
            amount: service.price,
            serviceTitle: service.title,
            customerName: form.fullName,
            phone: form.phone,
            email: form.email,
          }),
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Tạo thanh toán thất bại");
      }

      const data = await response.json();
      setPaymentInfo(data);

      toast.update(toastId, {
        render: "Tạo mã QR thành công! Vui lòng thanh toán ngay.",
        type: "success",
        isLoading: false,
        autoClose: 4000,
      });
    } catch (err: any) {
      console.error("Error creating payment:", err);
      toast.update(toastId, {
        render: err.message || "Không thể tạo thanh toán. Vui lòng thử lại.",
        type: "error",
        isLoading: false,
        autoClose: 6000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Đã sao chép: ${text}`, {
        autoClose: 2000,
        hideProgressBar: true,
      });
    } catch (err) {
      toast.error("Copy thất bại, vui lòng thử lại");
    }
  };

  const formatAccount = (account: string) => {
    return account
      .replace(/\D/g, "")
      .replace(/(\d{3})(?=\d)/g, "$1 ")
      .trim();
  };

  const formatVnd = (amount: number) => {
    return `${new Intl.NumberFormat("vi-VN", {
      maximumFractionDigits: 0,
    }).format(amount)} đ`;
  };

  const completePayment = async () => {
    if (!service || !paymentInfo) return;

    const toastId = toast.loading("Đang xác nhận thanh toán...", {
      position: "top-center",
    });

    try {
      const payload = {
        serviceId: Number((service as any).id),
        fullName: form.fullName || "Khách lẻ",
        phone: form.phone || "0000000000",
        email: form.email || "guest@example.com",
        paymentMethod: "VNPAY",
        transactionCode: paymentInfo.transaction.transactionCode,
      };

      // Gửi song song cả 2 API (không phụ thuộc lẫn nhau)
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:7001/api';
      const [internalResult, crmResult] = await Promise.allSettled([
        // Gửi đến API nội bộ
        fetch(`${apiUrl}/orders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }).then(async (res) => {
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.message || "Tạo đơn hàng thất bại");
          }
          return res.json();
        }),
        // Gửi đến CRM API
        crmApiService.sendOrder({
          fullName: form.fullName || "Khách lẻ",
          phone: form.phone || "0000000000",
          email: form.email || "guest@example.com",
          serviceId: Number((service as any).id),
          serviceTitle: service.title,
          paymentMethod: "VNPAY",
          paymentAmount: service.price,
        }),
      ]);

      // Kiểm tra kết quả
      if (internalResult.status === 'rejected') {
        console.error("Lỗi API nội bộ:", internalResult.reason);
      }
      if (crmResult.status === 'rejected') {
        console.warn("Lỗi CRM API:", crmResult.reason);
      }

      // Nếu ít nhất 1 trong 2 API thành công, hiển thị thành công
      const hasSuccess = 
        internalResult.status === 'fulfilled' || 
        crmResult.status === 'fulfilled';

      if (hasSuccess) {
        toast.update(toastId, {
          render: "Thanh toán thành công! Hoá đơn đã gửi về email của bạn.",
          type: "success",
          isLoading: false,
          autoClose: 6000,
        });

        setTimeout(() => onClose(), 2500); // Đợi toast hiện rồi mới đóng
      } else {
        // Cả 2 đều lỗi
        const errorMsg = internalResult.status === 'rejected' 
          ? (internalResult.reason as any)?.message || "Không thể xác nhận thanh toán. Vui lòng liên hệ admin@sdtc.vn"
          : "Không thể xác nhận thanh toán. Vui lòng liên hệ admin@sdtc.vn";
        toast.update(toastId, {
          render: errorMsg,
          type: "error",
          isLoading: false,
          autoClose: 8000,
        });
      }
    } catch (e: any) {
      console.error(e);
      toast.update(toastId, {
        render:
          e.message ||
          "Không thể xác nhận thanh toán. Vui lòng liên hệ admin@sdtc.vn",
        type: "error",
        isLoading: false,
        autoClose: 8000,
      });
    }
  };

  if (!isOpen || !service) return null;

  return (
    <div className={styles.pmBackdrop}>
      <div className={styles.pmModal}>
        <button
          className={styles.pmCloseIcon}
          onClick={onClose}
          aria-label="Đóng"
        >
          X
        </button>

        {isLoading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Đang xử lý...</p>
          </div>
        ) : paymentInfo ? (
          <>
            <div className={styles.pmCol}>
              <h3 className={styles.pmTitle}>Thông tin gói cước</h3>

              <div className={styles.pmRow}>
                <div className={styles.pmRowLabel}>Tên gói cước</div>
                <div className={styles.pmRowVal}>
                  <span className={styles.pmBlue}>{service.title}</span>
                </div>
              </div>

              <div className={styles.pmRow}>
                <div className={styles.pmRowLabel}>Giá trị thanh toán</div>
                <div className={styles.pmRowVal}>
                  <span className={styles.pmAmount}>
                    {formatVnd(paymentInfo.transaction.amount)}
                  </span>
                </div>
              </div>

              <div className={styles.pmRow}>
                <div className={styles.pmRowLabel}>Mã giao dịch</div>
                <div className={styles.pmRowVal}>
                  <span className={styles.pmBlue}>
                    {paymentInfo.transaction.transactionCode}
                  </span>
                  <button
                    onClick={() =>
                      copyToClipboard(paymentInfo.transaction.transactionCode)
                    }
                    className={styles.pmCopy}
                    title="Copy mã giao dịch"
                  />
                </div>
              </div>

              <div className={styles.pmRow}>
                <div className={styles.pmRowLabel}>Nhà cung cấp</div>
                <div className={styles.pmRowVal}>
                  <a
                    className={styles.pmLink}
                    href="https://sdtc.vn"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {paymentInfo.bank.accountName}
                  </a>
                </div>
              </div>

              <div className={styles.pmSep} />

              <div className={styles.pmRow}>
                <div className={styles.pmRowLabel}>Tên TK</div>
                <div className={styles.pmRowVal}>
                  <span className={styles.pmBlue}>
                    {paymentInfo.bank.accountName}
                  </span>
                </div>
              </div>

              <div className={styles.pmRow}>
                <div className={styles.pmRowLabel}>STK</div>
                <div className={styles.pmRowVal}>
                  <span className={styles.pmBlue}>
                    {formatAccount(paymentInfo.bank.accountNumber)}
                  </span>
                  <button
                    onClick={() =>
                      copyToClipboard(paymentInfo.bank.accountNumber)
                    }
                    className={styles.pmCopy}
                    title="Copy STK"
                  />
                </div>
              </div>

              <div className={styles.pmRow}>
                <div className={styles.pmRowLabel}>Ngân hàng</div>
                <div className={styles.pmRowVal}>
                  <span className={styles.pmBlue}>
                    {paymentInfo.bank.bankName}
                  </span>
                </div>
              </div>

              <div className={styles.pmRow}>
                <div className={styles.pmRowLabel}>Nội dung</div>
                <div className={styles.pmRowVal}>
                  <span className={styles.pmBlue}>
                    {paymentInfo.bank.content}
                  </span>
                  <button
                    onClick={() => copyToClipboard(paymentInfo.bank.content)}
                    className={styles.pmCopy}
                    title="Copy nội dung"
                  />
                </div>
              </div>
            </div>

            <div className={styles.pmQr}>
              <img src={paymentInfo.bank.vietQrUrl} alt="VietQR" />
              <div className={styles.pmNote}>
                Vui lòng kiểm tra đúng thông tin trước khi thanh toán
              </div>
              <div className={styles.pmEmail}>
                Kiểm tra hoá đơn sau khi thanh toán xong tại:{" "}
                <a 
                  className={styles.pmLink} 
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=congngherongbien@gmail.com&tf=cm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  admin@sdtc.vn
                </a>
              </div>
              <button className={styles.doneBtn} onClick={completePayment}>
                Hoàn thành
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.pmCol}>
              <h3 className={styles.pmTitle}>Thông tin gói cước</h3>

              <div className={styles.pmRow}>
                <div className={styles.pmRowLabel}>Tên gói cước</div>
                <div className={styles.pmRowVal}>
                  <span className={styles.pmBlue}>{service.title}</span>
                </div>
              </div>

              <div className={styles.pmRow}>
                <div className={styles.pmRowLabel}>Giá trị thanh toán</div>
                <div className={styles.pmRowVal}>
                  <span className={styles.pmAmount}>
                    {formatVnd(service.price || 0)}
                  </span>
                </div>
              </div>

              <div className={styles.pmSep} />

              <div className={styles.formGroup}>
                <label>Họ và tên</label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={(e) =>
                    setForm({ ...form, fullName: e.target.value })
                  }
                  placeholder="Nhập họ và tên"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Số điện thoại</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Nhập số điện thoại"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Nhập email"
                />
              </div>

              <button className={styles.createQrBtn} onClick={createPayment}>
                Tạo QR thanh toán
              </button>
            </div>

            <div className={styles.pmQr}>
              <div className={styles.pmNote}>
                Điền thông tin và bấm "Tạo QR thanh toán" để hiển thị mã QR
              </div>
              <div className={styles.pmEmail}>
                Mọi thắc mắc vui lòng liên hệ:
                <a 
                  className={styles.pmLink} 
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=congngherongbien@gmail.com&tf=cm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  congngherongbien@gmail.com
                </a>
              </div>
            </div>
          </>
        )}

        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          limit={1}
          style={{
            top: "20px",
            width: "90%",
            maxWidth: "420px",
            margin: "0 auto",
            zIndex: 9999,
          }}
          toastClassName="font-medium text-sm"
        />
      </div>
    </div>
  );
}
